import { isJentis } from './utils';
import type {
  JentisTrackingDimension3Params,
  JentisTrackingDefaultParams,
  JentisTrackingValueParams,
  JentisUserType,
  PageViewArgs,
} from './types';
import { isBrowser, SessionStorage } from '../../window';
import { JENTIS_PENDING_IDENTIFIER, TRACKING_ACTIONS } from './constants';
import { util } from '@pixum/std-lib';
import { PixumEnvUtils } from '../../pixumEnv';

/**
 * Pushes a pageview tracking event to Jentis.
 *
 * It uses the global `PIXUM_ENV.JENTIS.page` object (injected by Jentis),
 * enriches it with the current user and optional `pathName`, and optionally
 * includes a pending sourceModule/sourcePage from sessionStorage.
 *
 * Notice: The PIXUM_ENV.JENTIS is set in jentis in the code JENTIS -
 * Prepare Pageview Data without /order/success/
 *
 * @param user - Authenticated user info to include in tracking.
 * @param pathName - Optional pathname override (used as page name).
 */
export const pushPageview = (user: JentisUserType, pathName?: string): void => {
  if (!isBrowser()) return;

  const env = PixumEnvUtils.get('JENTIS');

  if (!env?.page) {
    console.warn('[Jentis] Missing PIXUM_ENV.JENTIS.page');

    return;
  }

  // clone to avoid mutating shared object
  const page = { ...env.page };

  if (pathName) {
    page['name'] = pathName;
  }

  const pageViewTrack: PageViewArgs = {
    track: 'pageview',
    page,
    user: {
      loginStatus: `${user.isAuthenticated}`,
      id: user.isAuthenticated ? user.id : '',
    },
    sourceModule: '',
    sourcePage: '',
  };

  const pending = SessionStorage.getItem(JENTIS_PENDING_IDENTIFIER);

  if (pending) {
    try {
      const { module, target } = JSON.parse(pending);
      pageViewTrack.sourceModule = module;
      pageViewTrack.sourcePage = target;
    } catch (err) {
      console.warn('[Jentis] Failed to parse pending session data:', err);
    }

    SessionStorage.removeItem(JENTIS_PENDING_IDENTIFIER);
  }

  window._jts?.push(pageViewTrack);
};

/**
 * Pushes a generic `submit` event to Jentis.
 *
 * This is typically used after a form or conversion-related action.
 * It checks for the presence of `PIXUM_ENV.JENTIS` before pushing to `_jts`.
 */
export const pushSubmit = (): void => {
  if (isBrowser() && PixumEnvUtils.get('JENTIS')) {
    window._jts?.push({ track: 'submit' });
  }
};

/**
 * Tracks a Jentis page view event using the current user context and path.
 *
 * @param path - The pathname of the visited page.
 */
export const trackPageView = (path?: string): void => {
  if (!PixumEnvUtils.isInitialized()) return;

  const user = PixumEnvUtils.get('USER');
  const jentisUser: JentisUserType = {
    id: user?.isAuthenticated ? user?.id || '' : '',
    isAuthenticated: user?.isAuthenticated || false,
  };

  pushPageview(jentisUser, path);
  pushSubmit();
};

/**
 * Tracks a generic event using Jentis.
 *
 * This function pushes a tracking object to the Jentis `_jts` data layer,
 * if Jentis is available in the current browser context.
 *
 * The event is structured with a fixed group ("generic"), a custom category (context),
 * and a name built from `action` and `label`.
 *
 * @param category - The event category (e.g. "Newsletter", "Product", "Navigation").
 * @param action - The event action, such as a user interaction or event type.
 * @param label - A descriptive label to provide additional context.
 *
 * @example
 * trackEvent('Newsletter', 'submit', 'HeaderForm');
 * // Sends: { track: 'event', group: 'generic', context: 'Newsletter', name: 'submit - HeaderForm' }
 */
export const trackEvent = (category: string, action: string | number, label: string): void => {
  // Use _jts (the push queue) rather than isJentis() which requires window.jentis.tracker.
  // Jentis loads its main script asynchronously, so tracker may not be ready on React mount.
  // _jts is set up synchronously and Jentis processes queued events once it fully loads.
  if (!isBrowser() || !window._jts) return;

  const params: JentisTrackingDefaultParams = {
    track: 'event',
    group: 'generic',
    context: category,
    name: `${action} - ${label}`,
  };

  window._jts.push(params);
};

/**
 * Memoized function to track scrolling events. It tracks the scroll position for 25%, 50%, 75%, and 100% of the document height.
 * This function uses a Set to ensure that each scroll position is only tracked once, preventing duplicate events. The returned
 * function is debounced to avoid excessive calls during scrolling.
 *
 * @returns A function that tracks the scroll position and sends events to Jentis.
 */
export function memoizeTrackScrolling(debounceInterval: number): () => void {
  const events = new Set<number>(); // Set to track already sent scroll events
  const allowedTriggers = [25, 50, 75, 100]; // Allowed scroll positions to trigger events

  /**
   * Tracks the scroll position and sends events to Jentis.
   */
  const trackScrolling = () => {
    const body = document.body;
    const html = document.documentElement;
    const documentHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight,
    );

    try {
      const endAreaOfDocument = documentHeight - window.innerHeight;
      let trigger = (window.scrollY / endAreaOfDocument) * 100 + 1;

      trigger = Math.floor(trigger / 25) * 25;

      if (allowedTriggers.includes(trigger) && !events.has(trigger)) {
        trackEvent(TRACKING_ACTIONS.SCROLLING, location.href, trigger.toString());
        events.add(trigger);
      }
    } catch {
      // ignore errors to have the least impact on the site
    }
  };

  return util.debounce(trackScrolling, debounceInterval, false);
}

/**
 * Tracks a link-out event when a user clicks an external link.
 *
 * @param {string} url - The URL the user is navigating to.
 *
 * This function sends a tracking event labeled "Link-Out" with the target URL
 * and the current host + path (from `util.parseUrl()`) as context.
 */
export const handleLinkOutTracking = (url: string) => {
  trackEvent('Link-Out', url, util.parseUrl().host + util.parseUrl().path);
};

/**
 * Sends a tracking event with a custom dimension3 to Jentis. This function is used to track logout actions
 *
 * @param category - The category of the event (e.g., 'User Actions').
 * @param action - The action being tracked (e.g., 'Logout').
 * @param label - A label for the event, providing additional context (e.g., 'User logged out').
 * @param dimension3 - A custom dimension value to be sent with the event (e.g., 'User ID or session info').
 * @returns
 */
export function sendDimension3Tracking(category: string, action: string, label: string, dimension3: string): void {
  if (!isJentis()) return;

  const params: JentisTrackingDimension3Params = {
    track: 'event',
    group: category,
    name: action,
    value: label,
    customDimension3: dimension3,
  };

  window._jts?.push(params);
}

/**
 * Tracks a software download event to Jentis.
 *
 * This function sends a specific download tracking event to the Jentis data layer
 * with the group "download" and name "software_download".
 *
 * Based on Gatsby implementation: packages/core/src/helpers/analytics/jentis.ts
 *
 * @example
 * trackDownloadSoftware();
 * // Sends: { track: 'event', group: 'download', name: 'software_download' }
 */
export function trackDownloadSoftware(): void {
  if (!isJentis()) return;

  const params: JentisTrackingDefaultParams = {
    track: 'event',
    group: 'download',
    name: 'software_download',
  };

  window._jts?.push(params);
}

/**
 * Tracks a cart_id_exists event to Jentis when the cart overview loads.
 *
 * Fires once on mount via CartIdTracker after the cartId is resolved from PSI.
 *
 * @param cartId - The cart identifier from shoppingcart.cart.getOverview.
 */
export function trackCartIdExists(cartId: string): void {
  if (!isJentis()) return;

  const params: JentisTrackingValueParams = {
    track: 'event',
    group: 'cart_id_exists',
    name: 'cart_id_exists',
    value: cartId,
  };

  window._jts?.push(params);
}
